import React, { useState, useEffect } from 'react'
import Modal from '../modal'

type Location = {
  id: string
  location_moniker: string
}

type Zone = {
  id: string
  zone_moniker: string
  locations?: Location[]
}

type ExistingCompletion = {
  id: string
  locationId: string
}

type ExistingZoneCompletion = {
  id: string
}

type Props = {
  isOpen: boolean
  zone: Zone
  workerName: string
  taskName: string
  existingCompletions?: ExistingCompletion[]
  existingZoneCompletion?: ExistingZoneCompletion
  onConfirm: (selectedLocationIds: string[], completionsToDelete: string[]) => void
  onConfirmZone: () => void
  onCancelZone: (completionId: string) => void
  onCancel: () => void
}

const DoneLocationModal: React.FC<Props> = ({
  isOpen,
  zone,
  workerName,
  taskName,
  existingCompletions = [],
  existingZoneCompletion,
  onConfirm,
  onConfirmZone,
  onCancelZone,
  onCancel
}) => {
  const [selectedLocations, setSelectedLocations] = useState<string[]>([])
  const [completionsToDelete, setCompletionsToDelete] = useState<string[]>([])

  // Pre-select existing completions when modal opens
  useEffect(() => {
    if (isOpen && existingCompletions.length > 0) {
      const existingLocationIds = existingCompletions.map(c => c.locationId)
      setSelectedLocations(existingLocationIds)
      setCompletionsToDelete([])
    } else if (isOpen) {
      setSelectedLocations([])
      setCompletionsToDelete([])
    }
  }, [isOpen, existingCompletions])

  const toggleLocation = (locationId: string) => {
    const existingCompletion = existingCompletions.find(c => c.locationId === locationId)

    if (selectedLocations.includes(locationId)) {
      // Unselecting
      setSelectedLocations(prev => prev.filter(id => id !== locationId))

      // If this was an existing completion, mark it for deletion
      if (existingCompletion) {
        setCompletionsToDelete(prev => [...prev, existingCompletion.id])
      }
    } else {
      // Selecting
      setSelectedLocations(prev => [...prev, locationId])

      // If this was marked for deletion, remove from delete list
      if (existingCompletion) {
        setCompletionsToDelete(prev => prev.filter(id => id !== existingCompletion.id))
      }
    }
  }

  const handleConfirm = () => {
    onConfirm(selectedLocations, completionsToDelete)
    setSelectedLocations([])
    setCompletionsToDelete([])
  }

  const handleCancel = () => {
    setSelectedLocations([])
    setCompletionsToDelete([])
    onCancel()
  }

  return (
    <Modal open={isOpen}>
      <div className="sm:items-start w-full max-w-[450px]">
        <div className="mt-4 sm:ml-4 sm:mt-0 sm:text-left">
          <h3 className="text-xl font-semibold leading-6 text-center pb-3 text-gray-900">
            Mark as Done
          </h3>

          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">
              <span className="font-medium">{workerName}</span> - {taskName}
            </p>
            <p className="text-sm text-gray-600">
              Zone: <span className="font-medium">{zone.zone_moniker}</span>
            </p>
          </div>

          {/* Location Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Locations (Optional)
            </label>
            <div className="flex flex-wrap gap-2">
              {zone.locations && zone.locations.length > 0 ? (
                zone.locations.map((location) => (
                  <button
                    key={location.id}
                    onClick={() => toggleLocation(location.id)}
                    className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      selectedLocations.includes(location.id)
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {location.location_moniker}
                  </button>
                ))
              ) : (
                <p className="text-sm text-gray-500">No locations available</p>
              )}
            </div>
            {selectedLocations.length > 0 && (
              <p className="text-xs text-gray-600 mt-2">
                {selectedLocations.length} location{selectedLocations.length > 1 ? 's' : ''} selected
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-2">
            {/* Location-specific completion button */}
            <button
              onClick={handleConfirm}
              className="w-full px-4 py-3 text-sm font-medium text-white bg-green-700 hover:bg-green-800 rounded-lg transition-colors"
            >
              Confirm Done
            </button>

            {/* Zone-level completion button */}
            {existingZoneCompletion ? (
              <button
                onClick={() => onCancelZone(existingZoneCompletion.id)}
                className="w-full px-4 py-3 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                Cancel Zone Completion
              </button>
            ) : (
              <button
                onClick={onConfirmZone}
                disabled={selectedLocations.length > 0}
                className={`w-full px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                  selectedLocations.length > 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                All Zone Confirm Done
              </button>
            )}

            <button
              onClick={handleCancel}
              className="w-full px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default DoneLocationModal
